import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Box, Typography, IconButton, useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  IoDocumentTextOutline,
  IoBriefcaseOutline,
  IoSchoolOutline,
  IoPeopleOutline,
  IoFolderOpenOutline,
  IoImagesOutline,
  IoNewspaperOutline,
} from "react-icons/io5";

const OPTIONS_PROFESSIONAL = [
  { id: "publicacion", label: "Publicación", icon: <IoDocumentTextOutline size={24} />, color: "#6C4DE2", desc: "Comparte una publicación en la comunidad", route: "/systems/comunidad?reset=true" },
  { id: "servicio", label: "Servicio", icon: <IoBriefcaseOutline size={24} />, color: "#059669", desc: "Ofrece un servicio profesional", route: "/systems/servicios?reset=true" },
  { id: "curso", label: "Curso", icon: <IoSchoolOutline size={24} />, color: "#ea580c", desc: "Crea un curso para la academia", route: "/systems/academia?reset=true" },
  { id: "proyecto", label: "Proyecto", icon: <IoFolderOpenOutline size={24} />, color: "#FF6B35", desc: "Crea un nuevo proyecto", route: "/systems/proyectos?reset=true" },
  { id: "portafolio", label: "Portafolio", icon: <IoImagesOutline size={24} />, color: "#8b5cf6", desc: "Agrega un ítem a tu portafolio", route: "/portfolio" },
  { id: "articulo", label: "Artículo / Noticia", icon: <IoNewspaperOutline size={24} />, color: "#dc2626", desc: "Escribe un artículo o noticia", route: "/systems/blog?reset=true" },
];

const OPTIONS_ENTERPRISE = [
  { id: "publicacion", label: "Publicación", icon: <IoDocumentTextOutline size={24} />, color: "#6C4DE2", desc: "Comparte una publicación de tu empresa", route: "/systems/comunidad?reset=true" },
  { id: "servicio", label: "Servicio", icon: <IoBriefcaseOutline size={24} />, color: "#059669", desc: "Ofrece un servicio de tu empresa", route: "/systems/servicios?reset=true" },
  { id: "curso", label: "Curso", icon: <IoSchoolOutline size={24} />, color: "#ea580c", desc: "Crea un curso para la academia", route: "/systems/academia?reset=true" },
  { id: "vacante", label: "Vacante", icon: <IoPeopleOutline size={24} />, color: "#0284c7", desc: "Publica una vacante laboral", route: "/systems/bolsa?reset=true" },
  { id: "proyecto", label: "Proyecto", icon: <IoFolderOpenOutline size={24} />, color: "#FF6B35", desc: "Crea un nuevo proyecto", route: "/systems/proyectos?reset=true" },
  { id: "articulo", label: "Artículo / Noticia", icon: <IoNewspaperOutline size={24} />, color: "#dc2626", desc: "Escribe un artículo o noticia", route: "/systems/blog?reset=true" },
];

const CreateNewModal = ({ open, onClose, profileType }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const options = profileType === "enterprise" ? OPTIONS_ENTERPRISE : OPTIONS_PROFESSIONAL;

  const handleOptionClick = (route) => {
    if (route) {
      navigate(route);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxWidth: 540,
          width: isMobile ? "100%" : "92%",
          maxHeight: isMobile ? "100%" : "85vh",
          m: isMobile ? 0 : 2,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          zIndex: 300000,
        },
      }}
      sx={{ zIndex: 300000 }}
    >
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 3,
          background: "linear-gradient(135deg, #6C4DE2 0%, #F21C63 100%)",
          color: "#fff",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.85)",
            bgcolor: "rgba(255,255,255,0.12)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: -0.5, mb: 0.5 }}>
          Crear nuevo
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 420 }}>
          {profileType === "enterprise"
            ? "Selecciona lo que quieres crear para tu empresa"
            : "Selecciona lo que quieres crear"}
        </Typography>
      </Box>

      <Box sx={{ px: 4, pt: 3, pb: 4, display: "flex", flexDirection: "column", gap: 1.5, overflow: "auto", maxHeight: isMobile ? "calc(100vh - 160px)" : "none", "&::-webkit-scrollbar": { width: 6, backgroundColor: "transparent" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#F21C63", borderRadius: 10, minHeight: 20 }, "&::-webkit-scrollbar-track": { backgroundColor: "transparent" }, scrollbarWidth: "thin", scrollbarColor: "#F21C63 transparent" }}>
        {options.map((opt) => (
          <Box
            key={opt.id}
            onClick={() => handleOptionClick(opt.route)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              p: 2,
              borderRadius: 3,
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: "1px solid #f0f0f0",
              bgcolor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              "&:hover": {
                bgcolor: `${opt.color}08`,
                borderColor: `${opt.color}40`,
                boxShadow: `0 8px 24px ${opt.color}20`,
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: `${opt.color}14`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: opt.color,
                flexShrink: 0,
                boxShadow: `0 4px 12px ${opt.color}20`,
              }}
            >
              {opt.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#211E26", lineHeight: 1.3, mb: 0.2 }}>
                {opt.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: "block" }}>
                {opt.desc}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: opt.color,
                flexShrink: 0,
                boxShadow: `0 0 6px ${opt.color}60`,
              }}
            />
          </Box>
        ))}
      </Box>
    </Dialog>
  );
};

export default CreateNewModal;
